import React, { useState, useEffect } from 'react';
import { useAuth } from '../auth/AuthContext';
import { appointmentApi } from '../utils/appointmentApi';
import { Toast, useToast } from '../utils';
import AppointmentScheduler from '../components/AppointmentScheduler';
import config from '../config';
import { Link } from 'react-router-dom';

export default function AppointmentsPage() {
  const { user } = useAuth();
  const { showToast, toastMessage, hideToast } = useToast(5000);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showScheduler, setShowScheduler] = useState(false);
  const [selectedClient, setSelectedClient] = useState('');
  const [clients, setClients] = useState([]);
  const [isSidebarOpen, setSidebarOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    fetchAppointments();
    fetchClients();
  }, []);

  const fetchAppointments = async () => {
    try {
      setLoading(true);
      const data = await appointmentApi.getMidwifeAppointments(user.sub);
      setAppointments(data);
    } catch (err) {
      setError('Failed to fetch appointments');
      showToast('Error loading appointments');
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await fetch(`${config.api.url}/midwives/${user.sub}/clients`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('velaToken')}`,
        },
      });
      if (!response.ok) throw new Error('Failed to fetch clients');
      const data = await response.json();
      setClients(data);
    } catch (err) {
      showToast('Error loading clients');
    }
  };

  const handleDeleteAppointment = async (appointmentId) => {
    if (isDeleting) return;
    
    if (!window.confirm('Are you sure you want to cancel this appointment?')) {
      return;
    }

    try {
      setIsDeleting(true);
      await appointmentApi.deleteAppointment(appointmentId);
      
      setAppointments(appointments.filter(apt => apt.id !== appointmentId));
      showToast('Appointment cancelled successfully');
    } catch (err) {
      console.error('Error cancelling appointment:', err);
      showToast(err.message || 'Error cancelling appointment. Please try again.');
      
      fetchAppointments();
    } finally {
      setIsDeleting(false);
    }
  };

  const formatDateTime = (appointment) => {
    try {
      const date = new Date(appointment.startTime);
      return date.toLocaleString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } catch (err) {
      console.error('Error formatting date:', appointment);
      return 'Invalid Date';
    }
  };

  return (
    <div className="flex flex-col md:flex-row min-h-screen bg-gray-100 text-gray-900">
      {/* Mobile App Bar */}
      <div className="md:hidden flex justify-between items-center bg-emerald-700 text-white px-4 py-3">
        <h1 className="text-2xl font-bold">Vela</h1>
        <button onClick={() => setSidebarOpen(!isSidebarOpen)}>☰</button>
      </div>

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-30 w-64 bg-gray-200 text-emerald-700
          p-6 space-y-4 transform transition-transform duration-300 ease-in-out
          md:relative md:translate-x-0 ${isSidebarOpen ? "translate-x-0" : "-translate-x-full"} md:block`}
      >
        <h1 className="text-3xl font-extrabold tracking-tight mb-6">Vela</h1>
        <nav className="flex flex-col gap-1 text-sm font-medium">
          {[
            { label: "Dashboard", to: "/midwife" },
            { label: "Clients", to: "/midwife/clients" },
            { label: "Appointments", to: "/midwife/appointments" },
            { label: "Messages", to: "/midwife/messages" },
            { label: "Charts", to: "/midwife/charts" },
            { label: "Labs", to: "/midwife/labs" },
            { label: "Documents", to: "/midwife/documents" },
            { label: "Settings", to: "/midwife/settings" }
          ].map(link => (
            <Link
              key={link.label}
              to={link.to}
              className="px-3 py-2 rounded
                         hover:bg-emerald-50 hover:text-emerald-800
                         transition-colors"
            >
              {link.label}
            </Link>
          ))}
        </nav>
      </aside>

      {/* Mobile overlay */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 z-20 bg-black/50 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content */}
      <main className="flex-1 p-4 sm:p-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-2xl font-bold text-emerald-700">Appointments</h1>
            <button
              onClick={() => setShowScheduler(true)}
              className="bg-emerald-600 text-white px-4 py-2 rounded-lg hover:bg-emerald-700"
            >
              Schedule New Appointment
            </button>
          </div>

          {/* Appointment Scheduler Modal */}
          {showScheduler && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
              <div className="bg-white rounded-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
                <div className="p-6">
                  <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-semibold">Schedule Appointment</h2>
                    <button
                      onClick={() => {
                        setShowScheduler(false);
                        setSelectedClient('');
                      }}
                      className="text-gray-500 hover:text-gray-700"
                    >
                      ×
                    </button>
                  </div>
                  
                  {/* Client Selection */}
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Select Client
                    </label>
                    <select
                      value={selectedClient}
                      onChange={(e) => setSelectedClient(e.target.value)}
                      className="w-full border-gray-300 rounded-md shadow-sm focus:ring-emerald-500 focus:border-emerald-500"
                    >
                      <option value="">Select a client...</option>
                      {clients.map(client => (
                        <option key={client.id} value={client.id}>
                          {client.firstName} {client.lastName}
                        </option>
                      ))}
                    </select>
                  </div>

                  {selectedClient && (
                    <AppointmentScheduler
                      clientId={selectedClient}
                      onScheduled={() => {
                        setShowScheduler(false);
                        setSelectedClient('');
                        fetchAppointments();
                      }}
                    />
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Appointments List */}
          <div className="bg-white rounded-xl shadow overflow-hidden">
            {loading ? (
              <div className="p-4 text-center">Loading appointments...</div>
            ) : error ? (
              <div className="p-4 text-red-600">{error}</div>
            ) : appointments.length === 0 ? (
              <div className="p-4 text-center text-gray-500">No appointments scheduled</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Client
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date & Time
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Type
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {appointments.map((appointment) => (
                      <tr key={appointment.id}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {appointment.clientName}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {formatDateTime(appointment)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap capitalize">
                          {appointment.type}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            appointment.status === 'scheduled'
                              ? 'bg-green-100 text-green-800'
                              : appointment.status === 'cancelled'
                              ? 'bg-red-100 text-red-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {appointment.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <button
                            onClick={() => handleDeleteAppointment(appointment.id)}
                            disabled={isDeleting}
                            className={`text-red-600 hover:text-red-900 disabled:opacity-50 disabled:cursor-not-allowed`}
                          >
                            {isDeleting ? 'Cancelling...' : 'Cancel'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </main>
      {toastMessage && <Toast message={toastMessage} onClose={hideToast} />}
    </div>
  );
} 